import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';
import Swal from 'sweetalert2/src/sweetalert2';
import { useRollbar, useRollbarPerson } from '@rollbar/react';

import Avatar from '@mui/material/Avatar';
import startCase from 'lodash/startCase';
import lowerCase from 'lodash/lowerCase';
import upperCase from 'lodash/upperCase';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { loadFormDefinition } from '@redux/modules/formGeneration';
import { getUserName, getUserMlsId } from '@redux/selectors/UserSelector';
import {
  checkForAutoSave,
  loadAutoSave,
  removeAutoSave,
  loadListingById,
  loadListingToCopyById,
  loadListingPhoto,
  loadListingPreferredPhotoDetails,
  reloadListingClicked,
} from '@redux/modules/listing';

import {
  selectCurrentListing,
  selectCurrentListingPreferredPhotoDetails,
  selectCurrentListingPhoto,
} from '@redux/selectors/ListingSelector';

import { listingDict } from '@redux/constants/GeneralConstants';

import useRowLock from '@hooks/useRowLock';
import useAcl from '@hooks/useAcl';
import useInterval from '@hooks/useInterval';

import Panel from '@components/smartAdminUI/Panel';
import Button from '@components/smartAdminUI/Button';
import Modal from '@components/smartAdminUI/Modal';
import Input from '@components/smartAdminUI/input/InputGroup';
import ShowingTimeWidget from '@components/widget/ShowingTimeWidget';
import StatusBadges from '@components/badges/StatusBadges';
import ViewLoader from '@components/loaders/ViewLoader';

import apiService from '@services/apiService';

const operationType = 'edit';
const definitionType = 'listings';

const listingAttributesWhiteList = [
  'ListingID',
  'PropertyType',
  'UnparsedAddress',
  'City',
  'StateOrProvince',
  'PostalCode',
];

const statusTasks = {
  chgActive: true,
  chgPending: true,
  chgSold: true,
  chgExp: true,
  chgWithdrawn: true,
  chgTerm: true,
};

const otherTasks = {
  chgDetails: true,
  chgPropertyType: true,
  chgLeased: true,
  chgMajor: true,
  chgDel: true,
};

const otherTaskConfig = [
  {
    taskCode: 'chgDetails',
    iconClass: 'far fa-file-edit fa-2x',
    buttonText: 'Change Details',
  },
  {
    taskCode: 'chgPropType',
    iconClass: 'far fa-exclamation-triangle fa-2x',
    buttonText: 'Change Property Type',
  },
  {
    taskCode: 'chgLeased',
    iconClass: 'far fa-house-leave fa-2x',
    buttonText: 'Change to Leased',
  },
  {
    taskCode: 'chgMajor',
    iconClass: 'far fa-database fa-2x',
    buttonText: 'Major Correction',
  },
  {
    taskCode: 'chgDel',
    iconClass: 'far fa-trash-alt fa-2x',
    buttonText: 'Delete Listing',
  },
];

const statusTaskConfig = [
  {
    taskCode: 'chgActive',
    buttonText: 'Active',
  },
  {
    taskCode: 'chgPending',
    buttonText: 'Pending',
  },
  {
    taskCode: 'chgSold',
    buttonText: 'Sold',
  },
  {
    taskCode: 'chgTerm',
    buttonText: 'Terminated',
  },
  {
    taskCode: 'chgExp',
    buttonText: 'Expired',
  },
  {
    taskCode: 'chgWithdrawn',
    buttonText: 'Withdrawn',
  },
];

const ViewListings = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [autoSaveButtonClicked, setAutoSaveButtonClicked] = useState(false);
  const [otherBtnArr, setOtherBtnArr] = useState([]);
  const [taskBtnArr, setTaskBtnArr] = useState([]);
  const [currentListingPhotoUrl, setCurrentListingPhotoUrl] = useState(null);
  const [hideButton, setHideButton] = useState(true);
  const [inputString, setInputString] = useState(''); // for ensuring the user of listing deletion
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [rowLockButtonClicked, setRowLockButtonClicked] = useState(false);
  const [editButtonClicked, setEditButtonClicked] = useState(false);
  const [deleteButtonClicked, setDeleteButtonClicked] = useState(false);
  const [canAccessShowingTime, setCanAccessShowingTime] = useState(false);
  const [canViewOpenHouses, setCanViewOpenHouses] = useState(false);

  const autoSaveExists = useSelector(state => state.listing.autoSaveExists);
  const currListing = useSelector(selectCurrentListing);
  const currentListingPhoto = useSelector(selectCurrentListingPhoto);
  const currentListingPreferredPhotoDetails = useSelector(selectCurrentListingPreferredPhotoDetails);

  const autoSaveCriteria = useMemo(() => ({ ResourceUnique: currListing?.UniqueID }), [currListing]);
  const rowLockEntity = useMemo(() => ({ type: 'listing', id: currListing?.UniqueID }), [currListing]);

  const [urlParam, setUrlParam] = useQueryParam('ListingID', StringParam);

  const listingCode = currListing?.PropertyType?.toString().toLowerCase();
  const listingType = useMemo(() => listingDict[listingCode], [listingCode]);
  const memoizedListingEntries = useMemo(() => (currListing ? Object.entries(currListing) : []), [currListing]);
  const username = useSelector(getUserName);
  const userMLSID = useSelector(getUserMlsId);

  const rollbar = useRollbar();
  useRollbarPerson({ id: userMLSID, username });

  const jsonBody = {
    where: {
      clause: [],
    },
  };

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    lockedByMe,
    lockedByMeWithinSession,
    impersonatedBy,
    isAdmin,
    lastUpdatedOn,
    lastCheckedOn,
    checkStatus,
    createLock,
    removeLock,
    discardChanges,
    AutoSaveMessage,
    LockMessage,
  } = useRowLock({ rowLockEntity });

  const checkAutoSave = () => {
    if (!autoSaveCriteria?.ResourceUnique) return;
    dispatch(checkForAutoSave(autoSaveCriteria));
  };

  useEffect(() => {
    checkAutoSave();
  }, [autoSaveCriteria]);

  /* If autoSave exists, then start to regularly check for autoSave changes. */
  useInterval(checkAutoSave, autoSaveExists ? (1000 * 60) : null);

  const {
    getListingAcl,
    canEditForm, canCopyForm, canAuditForm, formPermissions,
    isAllComplete,
  } = useAcl();
  const isAllAclComplete = isAllComplete();

  useEffect(() => {
    if (Object.keys(formPermissions).length > 0) {
      setCanAccessShowingTime(formPermissions.showingTime);
      setCanViewOpenHouses(formPermissions.openHouse);
    }
  }, [formPermissions]);

  useEffect(() => {
    if (urlParam) {
      jsonBody.where.clause.push({ name: 'ListingID', value: urlParam });
      dispatch(reloadListingClicked(jsonBody));
    }

    let otherButtons = Object.keys(otherTasks).map(key => {
      if (otherTasks[key]) return otherTaskConfig.find(btnCfg => btnCfg.taskCode === key);
      return null;
    });
    otherButtons = otherButtons.filter(btn => btn);
    setOtherBtnArr(otherButtons);

    let statusButtons = Object.keys(statusTasks).map(key => {
      if (statusTasks[key]) return statusTaskConfig.find(btnCfg => btnCfg.taskCode === key);
      return null;
    });
    statusButtons = statusButtons.filter(btn => btn);
    setTaskBtnArr(statusButtons);
  }, []);

  /*
    This has to be done using location. Currently, when the user loads a form, it redirects the user to the
      create/edit page. When this happens, it clears out the path and search values in the location object.
      We can't memo the location and use that as a comparison, because technically the locations will be
      the same on the reload, so we can't reset the search value in the location object.
  */
  useEffect(() => {
    if (location?.search === '') {
      setUrlParam(currListing?.ListingID, 'replaceIn');
    }
  }, [location]);

  useEffect(() => {
    if (currListing?.UniqueID) {
      // retrieves EDIT, COPY and AUDITLOG permissions
      getListingAcl({
        action: 'view',
        uniqueId: currListing.UniqueID,
      });

      // retrieves forms accessible to user
      getListingAcl({
        action: 'forms',
        uniqueId: currListing.UniqueID,
      });

      if (location?.search === '' || location?.search !== currListing?.ListingID) {
        setUrlParam(currListing?.ListingID, 'replaceIn');
      }
    }
  }, [currListing]);

  // for confirming deletion of listing by having the user type the string "delete" to confirm
  const confirmInput = typedString => {
    setInputString(typedString);

    if (typedString === 'delete' && hideButton) {
      setHideButton(false); // set submit button to enabled
    }
  };

  // Build where clause with UniqueID to retrieve records for
  const buildWhereClause = values => {
    const whereClause = {
      where: {
        clause: [],
      },
    };
    const valueKeys = Object.keys(values);

    valueKeys.map(key => {
      if (key === 'UniqueID') {
        whereClause.where.clause.push(
          {
            name: key,
            value: values[key],
          },
        );
      }
      return null;
    });
    return whereClause;
  };

  const loadAutoSaveData = () => {
    dispatch(loadFormDefinition(operationType, definitionType, listingCode, currListing?.UniqueID, 'listing')); // loads create listing page.
    dispatch(loadAutoSave(autoSaveCriteria));
  };

  const removeAutoSaveData = () => {
    dispatch(removeAutoSave(autoSaveCriteria));
  };

  /*
    When a listing is selected, form definition is loaded according to the PropertyType (listing type)
    Dispatch retrieves listing record based on UniqueID
 */
  const loadListingData = () => {
    dispatch(loadFormDefinition(operationType, definitionType, listingCode, currListing?.UniqueID, 'listing')); // loads create listing page.
    dispatch(loadListingById(buildWhereClause(currListing))); // dispatches call to get listing values
  };

  const onDiscardRowLock = () => {
    setRowLockButtonClicked(true);
    setEditButtonClicked(false);
    setDeleteButtonClicked(false);
    setAutoSaveButtonClicked(false);

    if (lockedByMe) {
      removeLock();
    } else {
      discardChanges();
    }

    // Force the AutoSave alert to display if an AutoSave exists by rechecking for AutoSave changes.
    if (autoSaveExists) {
      setRowLockButtonClicked(false);
      checkAutoSave();
    }
  };

  const onTakeoverAutoSave = () => {
    setAutoSaveButtonClicked(true);
    setEditButtonClicked(false);
    setDeleteButtonClicked(false);
    setRowLockButtonClicked(false);

    setIsFormLoading(true);
    createLock(loadAutoSaveData);
  };

  const onDiscardAutoSave = () => {
    setAutoSaveButtonClicked(true);
    setEditButtonClicked(false);
    setDeleteButtonClicked(false);
    setRowLockButtonClicked(false);

    removeAutoSaveData();
  };

  const onEditListing = () => {
    if (currListing) {
      loadListingData();
    }
  };

  const onDeleteListing = () => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      title: 'Delete Listing?',
      html: `Are you sure you want to delete Listing <strong>${currListing.ListingId}</strong>?`,
      iconHtml: '<i class="far fa-trash-alt fa-xs"/>',
      iconColor: 'var(--theme-danger)',
      confirmButtonText: 'Yes, Delete',
      denyButtonText: 'No, Cancel',
      showDenyButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn-danger btn-lg mr-2 swal-question-listing-discard-yes',
        denyButton: 'btn-primary btn-lg swal-question-listing-discard-no',
      },
    }).then(async result => {
      if (result.isConfirmed) {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          html: 'Deleting Listing...',
          title: '<span class="fa-stack fa-2x text-danger m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-trash-alt fa-stack-1x"></i></span>',
          showConfirmButton: false,
        });

        try {
          const response = await apiService.Listing.DeleteListingById({ UniqueID: currListing.UniqueID, PropertyType: currListing.PropertyType });
          const listingId = response?.Data?.ListingId;

          if (listingId) {
            setRowLockButtonClicked(false);
            setEditButtonClicked(false);
            setDeleteButtonClicked(true);
            setAutoSaveButtonClicked(false);

            if (autoSaveExists) removeAutoSaveData();
            if (lockedByMe) {
              removeLock();
            } else {
              discardChanges();
            }

            Swal.fire({
              allowEnterKey: false,
              allowEscapeKey: false,
              allowOutsideClick: false,
              position: 'center',
              title: 'Listing Deleted',
              text: `Listing ${currListing.ListingId} has been deleted.`,
              icon: 'success',
              iconColor: 'var(--theme-success)',
              showConfirmButton: false,
              timer: 3000,
            }).then(() => {
              history.push('/');
            });
          } else {
            Swal.fire({
              allowEnterKey: false,
              allowEscapeKey: false,
              allowOutsideClick: false,
              position: 'center',
              title: 'Listing Not Deleted',
              text: `Listing ${currListing.ListingId} could not be deleted at this time. Please contact support.`,
              icon: 'error',
              iconColor: 'var(--theme-danger)',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-danger btn-lg swal-fail-listing-delete',
              },
            });
          }
        } catch (err) {
          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Listing Not Deleted',
            text: `Listing ${currListing.ListingId} could not be deleted at this time. Please contact support.`,
            icon: 'error',
            iconColor: 'var(--theme-danger)',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-danger btn-lg swal-fail-listing-delete',
            },
          });
          rollbar.error('Error: Listing Delete', err, currListing.ListingId);
        }
      } else {
        setRowLockButtonClicked(false);
        setEditButtonClicked(false);
        setDeleteButtonClicked(true);
        setAutoSaveButtonClicked(false);

        if (autoSaveExists) removeAutoSaveData();
        if (lockedByMe) {
          removeLock();
        } else {
          discardChanges();
        }
      }
    });
  };

  const onCopyListing = () => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Copying Listing...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-copy fa-stack-1x"></i></span>',
      showConfirmButton: false,
      timer: 2000,
    });
    dispatch(loadFormDefinition('create', 'listings', currListing?.PropertyType?.toLowerCase(), null, 'listing', true));
    const copyRequest = buildWhereClause(currListing);
    copyRequest.formName = currListing?.PropertyType?.toLowerCase();
    dispatch(loadListingToCopyById(copyRequest));
  };

  const getTaskDefinitions = taskName => {
    dispatch(loadFormDefinition(operationType, definitionType, taskName, currListing?.UniqueID, 'listing'));
    dispatch(loadListingById(buildWhereClause(currListing)));
  };

  const getCurrentListingPreferredPhotoDetails = uniqueID => {
    dispatch(loadListingPreferredPhotoDetails(uniqueID));
  };

  const getCurrentListingPhoto = photoId => {
    dispatch(loadListingPhoto(photoId));
  };

  const onClickEdit = () => {
    setEditButtonClicked(true);
    setDeleteButtonClicked(false);
    setRowLockButtonClicked(false);
    setAutoSaveButtonClicked(false);

    setIsFormLoading(true);
    createLock(onEditListing);
  };

  const onClickDelete = () => {
    setEditButtonClicked(false);
    setDeleteButtonClicked(true);
    setRowLockButtonClicked(false);
    setAutoSaveButtonClicked(false);

    setIsFormLoading(true);
    createLock(onDeleteListing);
  };

  const onClickTask = taskCode => () => {
    setEditButtonClicked(true);
    setDeleteButtonClicked(false);
    setRowLockButtonClicked(false);
    setAutoSaveButtonClicked(false);
    setIsFormLoading(true);
    createLock(() => getTaskDefinitions(taskCode));
  };

  // temporary placeholder for copy functions
  const onCopyClick = () => {
    setRowLockButtonClicked(false);
    setAutoSaveButtonClicked(false);
    setIsFormLoading(true);
    onCopyListing();
  };

  const onAuditClick = () => {
    if (currListing.UniqueID !== null) {
      history.push(`/audit?ListingID=${currListing?.ListingID}`);
    }
  };

  // update change form buttons and edit button visibility depending on ACL permissions
  useEffect(() => {
    if (!listingCode) return;

    let otherButtons = Object.keys(formPermissions).flatMap(key => {
      if (!formPermissions[key]) return [];
      return otherTaskConfig.find(btnCfg => btnCfg.taskCode === key);
    });
    otherButtons = otherButtons.filter(btn => btn).sort((a, b) => otherTaskConfig.indexOf(a) - otherTaskConfig.indexOf(b));
    setOtherBtnArr(otherButtons);

    let taskButtons = Object.keys(formPermissions).flatMap(key => {
      if (!formPermissions[key]) return [];
      return statusTaskConfig.find(btnCfg => btnCfg.taskCode === key);
    });
    taskButtons = taskButtons.filter(btn => btn).sort((a, b) => statusTaskConfig.indexOf(a) - statusTaskConfig.indexOf(b));
    setTaskBtnArr(taskButtons);
  }, [formPermissions, listingCode]);

  useEffect(() => {
    if (currListing?.UniqueID) {
      getCurrentListingPreferredPhotoDetails(currListing.UniqueID);
    }
  }, [currListing]);

  useEffect(() => {
    if (currentListingPreferredPhotoDetails?.URL) {
      getCurrentListingPhoto(currentListingPreferredPhotoDetails.URL);
    }
  }, [currentListingPreferredPhotoDetails]);

  useEffect(() => {
    if (currentListingPhotoUrl) URL.revokeObjectURL(currentListingPhotoUrl);
    if (currentListingPhoto) setCurrentListingPhotoUrl(URL.createObjectURL(currentListingPhoto));
    return () => currentListingPhotoUrl && URL.revokeObjectURL(currentListingPhotoUrl);
  }, [currentListingPhoto]);

  if (currListing) { // added check to avoid crash when home button was clicked.
    return (
      <>
        <Modal
          id="DeleteConfirmModal"
          position="center"
          modalHeader="Delete Confirmation"
        >
          <div className="alert alert-danger">
            <div className="d-flex align-items-center">
              <div className="alert-icon">
                <i className="fal fa-info-circle" />
              </div>
              <div>
                <h4>Are you sure?</h4>
                <p>You are about to permanently delete a listing and all its contents.</p>
              </div>
            </div>
          </div>
          <p>
            Please confirm by typing
            <span className="text-danger font-weight-bold">delete</span>
            below
          </p>
          <Input
            type="text"
            value={inputString}
            placeholder="Please confirm deletion..."
            onChange={e => confirmInput(e.target.value)}
          />
          <div className="modal-footer">
            <button name="btnCancelDeleteListing" type="button" className="btn btn-default waves-effect waves-themed" data-target="#DeleteConfirmModal" data-dismiss="modal">Cancel</button>
            <button name="btnConfirmDeleteListing" disabled={hideButton} type="submit" className="btn btn-danger waves-effect waves-themed">Confirm Delete</button>
          </div>
        </Modal>
        <div className="subheader">
          <h1 className="subheader-title">
            <i className="subheader-icon fal fa-ballot mr-2" />
            View
            <span className="fw-300 ml-2 mr-2">Listing</span>
          </h1>
        </div>
        {!isAllAclComplete && !canEditForm ? (
          <ViewLoader type={definitionType} />
        ) : (
          <>
            {lockedBy && impersonatedBy && ((lockedByMeWithinSession && !editButtonClicked && !deleteButtonClicked) || (locked && (!lockingAuthorized || (rowLockButtonClicked && isFormLoading)))) && (
              <LockMessage
                rowLockEntity={rowLockEntity}
                lockedBy={lockedBy}
                lockedByMeWithinSession={lockedByMeWithinSession}
                impersonatedBy={impersonatedBy}
                userName={username}
                isAdmin={isAdmin}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onDiscard={onDiscardRowLock}
                displayActions={canEditForm}
              />
            )}
            {(autoSaveExists || (autoSaveButtonClicked && isFormLoading)) && (
              <AutoSaveMessage
                isAdmin={isAdmin}
                recordIsLocked={locked}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onTakeover={onTakeoverAutoSave}
                onDiscard={onDiscardAutoSave}
                displayActions={((locked && lockedByMeWithinSession) || (!locked && autoSaveExists)) && canEditForm}
              />
            )}
            <div className="row">
              <div className="col-lg-4 d-flex">
                <Panel customClass="w-100" customBodyClass={locked && !lockingAuthorized ? 'disabled' : ''}>
                  <div className="d-flex flex-column align-items-center justify-content-center p-4">
                    <Avatar
                      sx={{
                        minHeight: '250px',
                        maxHeight: '250px',
                        width: '100%',
                        bgcolor: 'var(--theme-secondary)',
                      }}
                      variant="rounded"
                      alt={currListing.ListingID}
                      src={currentListingPhotoUrl}
                    >
                      <i className="fas fa-home fa-6x" />
                    </Avatar>
                  </div>
                  <h3 className="mb-1 text-center">
                    {isEmpty(currListing.UnitNumber) ? '' : `${currListing.UnitNumber}, `}
                    {`${currListing.StreetNumber} ${startCase(lowerCase(currListing.StreetName))} ${startCase(lowerCase(currListing.StreetSuffix))}`}
                    {isEmpty(currListing.StreetDirSuffix) ? '' : ` ${upperCase(currListing.StreetDirSuffix)}`}
                  </h3>
                  <h4 className="mb-4 text-center">
                    {isEmpty(currListing.City) ? '' : `${startCase(currListing.City)}, `}
                    {startCase(currListing.StateOrProvince) ?? ''}
                  </h4>
                  <p className="mb-2 text-center">
                    <StatusBadges status={currListing.StandardStatusVerb} />
                    <span className="ml-2">{currListing.ListingID}</span>
                  </p>
                </Panel>
              </div>
              <div className="col-lg-8">
                <Panel
                  header="Listing Details"
                  customClass="w-100"
                  customBodyClass={locked && !lockingAuthorized ? 'disabled' : ''}
                  panelToolbar={(
                    <>
                      {canEditForm && otherBtnArr.find(x => x.taskCode === 'chgDel') !== undefined && (
                        <Button
                          name="btnDeleteListing"
                          variant="danger"
                          onClick={onClickDelete}
                          disabled={locked && !lockingAuthorized}
                        >
                          <i className="far fa-trash-alt mr-2" />
                          Delete
                        </Button>
                      )}
                      {canEditForm && (
                        <Button
                          name="btnEditListing"
                          variant="primary"
                          onClick={onClickEdit}
                          disabled={(locked && !lockingAuthorized) || autoSaveExists}
                        >
                          <i className="far fa-edit mr-2" />
                          Edit
                        </Button>
                      )}
                      {canCopyForm && (
                        <Button
                          name="btnCopy"
                          variant="primary"
                          onClick={onCopyClick}
                          disabled={autoSaveExists}
                        >
                          <i className="far fa-copy mr-2" />
                          Copy
                        </Button>
                      )}
                      {canAuditForm && (
                        <Button
                          name="btnAuditListing"
                          variant="primary"
                          onClick={onAuditClick}
                        >
                          <i className="far fa-history mr-2" />
                          Audit
                        </Button>
                      )}
                    </>
                  )}
                >
                  <table className="table borderless">
                    <tbody>
                      {
                        sortBy(memoizedListingEntries).map(([key, value]) => {
                          if (!listingAttributesWhiteList.includes(key)) {
                            return null;
                          }
                          if (value) {
                            return (
                              <tr key={key}>
                                <th className="w-50">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}</th>
                                <td>{ (key === 'PropertyType' ? listingType : value) }</td>
                              </tr>
                            );
                          }
                          return null;
                        })
                      }
                      {memoizedListingEntries.length === 0 && 'No Values to Show'}
                    </tbody>
                  </table>
                </Panel>
                {canEditForm && taskBtnArr.length > 0 && (
                  <Panel
                    panelToolbar="Status"
                    customClass="w-100"
                  >
                    <div className="row d-flex">
                      {taskBtnArr.map(element => (
                        <div
                          key={element.taskCode}
                          className="h-100 text-center m-2"
                        >
                          <p><StatusBadges status={element.buttonText} /></p>
                          <Button
                            name={`btn${element.buttonText}Listing`}
                            onClick={onClickTask(element.taskCode)}
                            className="btn-block width-sm"
                            disabled={locked && !lockingAuthorized}
                          >
                            <i className="far fa-chevron-right mr-2" />
                            {`${element.buttonText}`}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Panel>
                )}
              </div>
            </div>
            {canEditForm && (
              <div className={locked && !lockingAuthorized ? 'row disabled' : 'row'}>
                {canAccessShowingTime && (
                  <div className="col-lg-4 d-flex">
                    <Panel customClass="w-100">
                      <ShowingTimeWidget disabled={(locked && !lockingAuthorized) || autoSaveExists} />
                    </Panel>
                  </div>
                )}
                {canViewOpenHouses && (
                  <div className={canAccessShowingTime ? 'col-lg-3' : 'col-lg-4'}>
                    <Panel>
                      <Button
                        name="btnChangeOpenHouseListing"
                        key="chgOpenHouse"
                        onClick={onClickTask('chgOpenHouse')}
                        variant="primary"
                        className="mr-2 mb-2"
                        disabled={(locked && !lockingAuthorized) || autoSaveExists}
                      >
                        <i className="far fa-home fa-2x" />
                        <p>Open Houses</p>
                      </Button>
                    </Panel>
                  </div>
                )}
                {(otherBtnArr.length > 1 || (otherBtnArr.length === 1 && otherBtnArr[0].taskCode !== 'chgDel')) && (
                  // eslint-disable-next-line no-nested-ternary
                  <div className={canAccessShowingTime && canViewOpenHouses ? 'col-lg-5' : (!canAccessShowingTime && !canViewOpenHouses ? 'col-lg-12' : 'col-lg-8')}>
                    <Panel>
                      {otherBtnArr.map(element => (element.taskCode !== 'chgDel' && (
                        <Button
                          name={`btn${element.buttonText}Listing`}
                          key={element.taskCode}
                          onClick={onClickTask(element.taskCode)}
                          variant="primary"
                          className="mr-2 mb-2"
                          disabled={(locked && !lockingAuthorized) || autoSaveExists}
                        >
                          <i className={`${element.iconClass}`} />
                          <p>{`${element.buttonText}`}</p>
                        </Button>
                      )))}
                    </Panel>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </>
    );
  }
  return null;
};

export default ViewListings;
